import { isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';

/** Compact cart editors use a native modal, including inside the cart drawer. */
class CartToolDialog extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-cart-tool-open]')) this.show();
      else if (event.target.closest('[data-cart-tool-close]')) this.close();
      else if (dialog.open && event.target === dialog && isClickedOutside(event, dialog)) this.close();
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !dialog.open || event.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }, { signal });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });
    dialog.addEventListener('close', () => {
      // The drawer closes and reopens nested modals when crossing its breakpoint.
      if (!dialog.open) unlockScroll(dialog);
    }, { signal });
  }

  show() {
    const dialog = this.querySelector('dialog');
    if (!dialog || dialog.open) return;
    const note = this.closest('cart-note');
    const textarea = dialog.querySelector('textarea');
    if (note && textarea) textarea.value = note.querySelector('[data-cart-note-preview]')?.textContent || '';
    const status = dialog.querySelector('[data-cart-note-status]');
    if (status) status.textContent = '';
    dialog.showModal();
    lockScroll(dialog);
    dialog.querySelector('textarea, input')?.focus({ preventScroll: true });
  }

  close({ force = false } = {}) {
    if (!force && this.closest('[aria-busy="true"]')) return;
    const dialog = this.querySelector('dialog');
    if (!dialog?.open) return;
    dialog.close();
    unlockScroll(dialog);
    this.querySelector('[data-cart-tool-open]')?.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    const dialog = this.querySelector('dialog');
    if (dialog) unlockScroll(dialog);
    this.#controller?.abort();
  }
}

if (!customElements.get('cart-tool-dialog')) customElements.define('cart-tool-dialog', CartToolDialog);
